<template>
  <div>
    <div
      class="text-center"
      v-if="!dataLoaded"
    >
      <v-progress-circular
        class="mt-6"
        :size="50"
        color="primary"
        indeterminate
      />
    </div>

    <div
      class="mt-4"
      v-show="dataLoaded"
    >
      <TitleCard
        :is-show-mode="isShowMode"
        :is-process-mode="isProcessMode"
        :is-edit-mode="isEditMode"
        :disabled="invalid"
        :saveLoading="saveBtnLoading"
        :selected-item="contest"
        :is-no-changes="isNoChanges"
        @save="saveContest"
      />

      <v-row
        class="mb-0 mt-2"
        style="margin-right: -14px"
      >
        <v-col
          ref="workList"
          cols="12"
          class="pt-1 pb-2 pr-2 overflow-auto"
        >
          <WorkTitleCard :title="workTypeRusName(workTypeName.Contest)" />

          <AuthorsCard
            class="mt-4"
            title="Автор"
            :agreementFileList="agreementFileList"
            :selected-item.sync="contest.commonWork.authors"
            :is-show-mode="isShowMode"
            :max-authors="10"
            :upload-files="true"
            @updateInvalid="updateAuthorsInvalid"
            @uploadAgreementFile="initUploadAgreementFile"
            @removeAgreementFile="initRemoveAgreementFile"
          />

          <ContestCard
            class="mt-4"
            :linkFileList="linkFileList"
            title="Основная информация"
            :selected-item.sync="contest"
            :is-show-mode="isShowMode"
            @updateInvalid="updateContestInvalid"
            @uploadLinkFiles="initUploadLinkFiles"
            @removeLinkFile="initRemoveLinkFile"
          />

          <CommentCard
            v-if="Boolean(contest.commonWork.comment)"
            class="mt-4"
            :comment="contest.commonWork.comment"
          />

          <CreatorCard :creator-name="contest.commonWork.creator.fullName" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { contestApi } from '@/api'
import TitleCard from '../../components/Works/elements/TitleCard'
import { Contest } from '../../models'
import ContestCard from '../../components/Works/Contest/ContestCard'
import AuthorsCard from '../../components/Works/elements/AuthorsCard'
import CommentCard from '@/components/Works/elements/CommentCard.vue'
import { mapActions, mapGetters } from 'vuex'
import { workListHeight } from '@/mixins/workListHeight'
import { filesForWork } from '@/mixins/filesForWork'
import { dictionaryNames, fileTypeId, workMode, workTypeName } from '@/scripts/consts'
import WorkTitleCard from '@/components/Works/elements/WorkTitleCard.vue'
import CreatorCard from '@/components/Works/elements/CreatorCard.vue'

export default {
  name: 'ContestParent',

  metaInfo() {
    return {
      title: this && this.workTypeRusName(workTypeName.Contest)
    }
  },

  components: {
    CreatorCard,
    CommentCard,
    AuthorsCard,
    ContestCard,
    TitleCard,
    WorkTitleCard
  },

  mixins: [workListHeight, filesForWork],

  created() {
    this.isShowMode = this.$route.params.mode === workMode.Show
    this.isProcessMode = this.$route.params.mode === workMode.Process
    this.isEditMode = this.$route.params.mode === workMode.Edit
    this.contest = new Contest({
      commonWork: {
        isEffContract: true
      }
    })
    this.getData()
  },

  data: () => ({
    contest: new Contest(),
    saveBtnLoading: false,
    dataLoaded: false,
    contestInvalid: false,
    authorsInvalid: false,
    isShowMode: false,
    isEditMode: false,
    isProcessMode: false,
    isNoChanges: true
  }),

  computed: {
    workTypeName() {
      return workTypeName
    },
    ...mapGetters('workTypes', ['workTypeByName', 'workTypeRusName']),

    invalid() {
      return this.authorsInvalid || this.contestInvalid
    }
  },

  methods: {
    ...mapActions('dictionaries', ['getDictionaries']),
    ...mapActions('workTypes', ['getWorkTypes']),

    async getData() {
      if (!this.isShowMode)
        await this.getDictionaries([
          dictionaryNames.NirType,
          dictionaryNames.ScientDepartment,
          dictionaryNames.NirLevel,
          dictionaryNames.ContestResult
        ])
      await this.getWorkTypes()
      await this.getContestBody()
      this.dataLoaded = true
      this.isNoChanges = true
    },

    async getContestBody() {
      try {
        if (this.$route.params?.id !== '0') {
          const data = await contestApi.get(this.$route.params?.id)
          await this.getFileList(data.commonWork?.id)
          this.contest = new Contest(data)
        }
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Не удалось получить данные по работе')
      }
    },

    async saveContest(isThrowError = false) {
      try {
        this.saveBtnLoading = true

        if (!this.contest.id) this.contest.commonWork.workType = this.workTypeByName(workTypeName.Contest)

        const body = this.contest.getForAPI()

        const data = this.contest.id ? await contestApi.update(body) : await contestApi.create(body)

        await this.removeFileFromAPI()

        const linkFileListForSave = this.linkFileList.filter(file => !file.id)
        const agreementFileListForSave = this.agreementFileList.filter(file => !file.id)

        if (linkFileListForSave.length) await this.saveFile(data?.commonWork?.id, linkFileListForSave, fileTypeId.link)
        if (agreementFileListForSave.length)
          await this.saveFile(data?.commonWork?.id, agreementFileListForSave, fileTypeId.agreement)

        if (!this.contest.id) await this.$router.replace('/works/contest/edit/' + data.id)

        await this.getFileList(data.commonWork?.id)
        this.contest = new Contest(data)
        this.$toast.info('Работа успешно сохранена')
        queueMicrotask(() => (this.isNoChanges = true))
      } catch (e) {
        console.log(e)
        if (e.message === 'Ошибка удаления файлов') this.getFileList(this.contest.commonWork?.id)
        else this.showErrorMessage(e, 'Ошибка сохранения работы')
        if (isThrowError) throw new Error('Ошибка сохранения')
      } finally {
        this.saveBtnLoading = false
      }
    },

    initUploadLinkFiles(files) {
      this.uploadLinkFiles(files)
      this.isNoChanges = false
    },

    initRemoveLinkFile(index) {
      this.removeLinkFile(index)
      this.isNoChanges = false
    },

    initUploadAgreementFile(file) {
      this.uploadAgreementFile(file)
      this.isNoChanges = false
    },

    initRemoveAgreementFile(index) {
      this.removeAgreementFile(index)
      this.isNoChanges = false
    },

    async updateAuthorsInvalid(val) {
      this.authorsInvalid = val
      this.isNoChanges = false
    },

    async updateContestInvalid(val) {
      this.contestInvalid = val
      this.isNoChanges = false
    }
  },

  watch: {
    dataLoaded() {
      this.setWorkListHeight()
    }
  },

  provide: function () {
    return {
      saveWork: this.saveContest
    }
  }
}
</script>

<style scoped></style>
